import { useRef, useMemo, useCallback } from 'react';
import { Panel, ReactTable, BrowserIcon, OSIcon, SkeletonLine } from '@crazyegginc/hatch';
import { SearchInput } from '/src/components/search-input';
import classNames from 'classnames';
import ReactCountryFlag from 'react-country-flag';
import { formatBrowserName, percentizeValue } from '../utils';
import { msToHumanDuration } from '/src/utils/date';
import { ReactComponent as LinkIcon } from '@crazyegginc/hatch/dist/images/icon-open-url-outline.svg';
import { ReactComponent as NoResultsIcon } from '@crazyegginc/hatch/dist/images/icon-no-results-outline.svg';
import { useQueryParams } from '/src/hooks';
import { useAtom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

const linkCell = ({ selectedSite, path }) => {
  return (
    <a
      href={`https://${selectedSite.name}${path}`}
      target="_blank"
      className="flex items-center space-x-2 truncate"
      rel="noreferrer"
    >
      <span className="truncate">{path}</span>
      <LinkIcon className="flex-shrink-0 fill-current h-3 w-3 opacity-50" aria-label="external" />
    </a>
  );
};

const labelColumn = (header, accessorKey = 'page', cellComponent = null) => {
  return {
    header,
    accessorKey,
    size: 150,
    meta: {
      align: 'center',
      justify: 'left',
      headerCellClassnames: '!pl-0 !normal-case !text-body-1',
      rowCellClassnames: '!pl-0',
    },
    cell: cellComponent
      ? cellComponent
      : function Cell({ row }) {
          return <span className="truncate">{row.original[accessorKey]}</span>;
        },
  };
};

const simpleColumn = (header, accessorKey, mutator = null, size = 40) => {
  return {
    header,
    accessorKey,
    size,
    meta: {
      align: 'center',
      justify: 'right',
      headerCellClassnames: '!normal-case',
    },
    cell: function Cell({ row }) {
      if (mutator) {
        return mutator?.(row.original[accessorKey]);
      }
      return row.original[accessorKey];
    },
  };
};

const bounceRateColumn = simpleColumn('Bounce rate', 'bounceRate', (value) => percentizeValue(value, '-'), 50);
const durationMutator = (value) => (value ? msToHumanDuration(value) : '-');

const categoricalMetricColumns = (title, customLabelComponent) => [
  labelColumn(title, 'name', customLabelComponent),
  simpleColumn('Visitors', 'visitors'),
  simpleColumn('Pageviews', 'pageViews'),
  simpleColumn('Sessions', 'sessions'),
  simpleColumn('Avg session duration', 'avgSessionDuration', durationMutator, 60),
  bounceRateColumn,
];

function browserCellLabel({ row }) {
  const { name } = row.original;

  return (
    <div className="flex items-center space-x-2 truncate">
      <BrowserIcon
        className="w-4 h-4"
        browser={name.toUpperCase().replaceAll(' ', '_')}
        variant="COLOR"
        renderUnknown={true}
      />
      <span className="mt-0.5 truncate">{formatBrowserName(name)}</span>
    </div>
  );
}

function osCellLabel({ row }) {
  const { name } = row.original;

  return (
    <div className="flex items-center space-x-2 truncate">
      <OSIcon className="w-4 h-4" os={name.toUpperCase().replaceAll(' ', '_')} variant="COLOR" renderUnknown={true} />
      <span className="mt-0.5 truncate">{name}</span>
    </div>
  );
}

function getPlaceholder(view) {
  switch (view) {
    case 'deviceType':
      return 'Search devices';
    case 'os':
      return 'Search OSes';
    case 'entryPages':
      return 'Search pages';
    case 'utmMediums':
      return 'UTM Mediums';
    case 'utmContents':
      return 'UTM Content';
    case 'utmTerms':
      return 'UTM Terms';
    case 'utmCampaigns':
      return 'UTM Campaigns';
    default:
      return `Search ${view}`;
  }
}

export function getDefaultOrder() {
  const params = new URLSearchParams(window.location.search);
  const view = window.location.pathname.split('/').at(-1);

  const [column, order] = params.get('order')?.split?.('_') ?? [null, null];

  if (column && order) {
    return { id: column, desc: order === 'desc' };
  }

  switch (view) {
    case 'pages':
    case 'utmTerms':
    case 'utmCampaigns':
    case 'utmContents':
    case 'utmMediums':
    case 'utmSources':
      return { id: 'pageViews', desc: true };
    default:
      return { id: 'visitors', desc: true };
  }
}

export const columnOrderAtom = atomWithDefault(() => {
  getDefaultOrder();
});

export const searchAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  return params.get('search') || '';
});

export function AnalyticsTable({ selected = null, analytics = [], title, selectedSite, isLoading = false }) {
  const tableRef = useRef(null);
  const { set: paramSet, remove: paramRemove } = useQueryParams();
  const [sortOrder, setSortOrder] = useAtom(columnOrderAtom);
  const [search, setSearch] = useAtom(searchAtom);

  const columns = useMemo(() => {
    switch (selected) {
      case 'deviceType':
        return categoricalMetricColumns('Device');
      case 'countries':
        return [
          labelColumn(title, 'name', function CountryTitle({ row }) {
            return (
              <div className="flex items-center space-x-2 truncate">
                <ReactCountryFlag
                  countryCode={row.original.code}
                  svg
                  alt={row.original.name}
                  style={{
                    width: '18px',
                    height: '18px',
                    border: '1px solid #e1e6ef',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    overflow: 'hidden',
                  }}
                />
                <span className="mt-0.5 truncate">{row.original.name}</span>
              </div>
            );
          }),
          simpleColumn('Visitors', 'visitors'),
          simpleColumn('Pageviews', 'pageViews'),
          simpleColumn('Sessions', 'sessions'),
          simpleColumn('Avg session duration', 'avgSessionDuration', durationMutator, 60),
          bounceRateColumn,
        ];
      case 'languages':
        return categoricalMetricColumns('Language');
      case 'os':
        return categoricalMetricColumns('OS', osCellLabel);
      case 'browsers':
        return categoricalMetricColumns('Browser', browserCellLabel);
      case 'channels':
        return categoricalMetricColumns('Channel');
      case 'referrers':
        return categoricalMetricColumns('Referrer');
      case 'campaigns':
        return categoricalMetricColumns('Campaign');
      case 'entryPages':
        return [
          labelColumn('Page', 'name', ({ row }) => linkCell({ path: row.original.name, selectedSite })),
          simpleColumn('Visitors', 'visitors'),
          simpleColumn('Pageviews', 'pageViews'),
          simpleColumn('Avg time on page', 'avgTimeOnPage', durationMutator, 55),
          simpleColumn('Sessions', 'sessions'),
          simpleColumn('Avg session duration', 'avgSessionDuration', durationMutator, 70),
          bounceRateColumn,
        ];
      default:
        return [
          labelColumn('Page', 'name', ({ row }) => linkCell({ path: row.original.name, selectedSite })),
          simpleColumn('Pageviews', 'pageViews'),
          simpleColumn('Avg time on page', 'avgSessionDuration', durationMutator),
        ];
    }
  }, [selected, title, selectedSite]);

  const searchFunc = useCallback(
    (value) => {
      const parsedValue = (value || '').trim();
      if (parsedValue.length > 0) {
        paramSet('search', encodeURIComponent(parsedValue));
        setSearch(parsedValue);
      } else {
        paramRemove('search');
        setSearch('');
      }
    },
    [paramSet, paramRemove, setSearch],
  );

  const onFetchData = useCallback(
    ({ sorting = null }) => {
      if (sorting) {
        const nextField = sorting[0].id;
        const nextSort = sorting[0].desc ? 'desc' : 'asc';
        paramSet('order', `${nextField}_${nextSort}`);
        setSortOrder({
          id: nextField,
          desc: nextSort === 'desc',
        });
      }
    },
    [setSortOrder, paramSet],
  );

  const hasNoResults = (analytics?.length ?? 0) === 0 && !isLoading;

  return (
    <Panel className={classNames('!p-0', { 'min-h-[314px]': hasNoResults })}>
      <div className="flex items-center justify-between w-full px-5 py-5 flex-shrink-0">
        <div className="flex items-center space-x-1">
          <h1 className="text-header-2">{title}</h1>
          {isLoading ? (
            <span className="w-20">
              <SkeletonLine />
            </span>
          ) : (
            <span className="text-xl font-semibold text-charcoal-gray-500 text-opacity-50">
              {analytics?.length ?? 0}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* <Button variant="secondary" className="flex-shrink-0 !h-10"> */}
          {/*   Download CSV */}
          {/* </Button> */}
          <SearchInput
            initialValue={search}
            placeholder={getPlaceholder(selected)}
            search={searchFunc}
            className="!h-10 !bg-solitude-500 !border-solitude-500 hover:border-dodger-blue-500 focus:border-dodger-blue-500 placeholder-dodger-blue-500"
          />
        </div>
      </div>
      <div className="relative w-full flex-1">
        <div className="z-10">
          <ReactTable
            fetching={isLoading}
            ref={{ tableRef }}
            rowPadding={false}
            columns={columns}
            data={analytics}
            onFetchData={onFetchData}
            enableSorting={true}
            sorting={sortOrder}
            useVirtualization={true}
            className="!border-none !shadow-none"
            headerCustomClassname="!pl-5 pr-[30px]"
            rowCustomClassname={() =>
              classNames(
                '!pl-5 pr-[30px] odd:bg-[#FAFAFA] after:!border-[#EAEAEA] after:!border-solid after:!left-0 after:!right-0',
              )
            }
          />
        </div>
        {hasNoResults ? (
          <div className="absolute w-full h-full top-0 left-0 z-50 bg-white bg-opacity-60 flex justify-center items-end">
            <div className="flex flex-col items-center mb-[65px]">
              <NoResultsIcon className="w-8 h-8 opacity-50 mb-2.5" />
              {search && search !== '' ? (
                <span className="text-body-2">
                  Zero results for <strong className="text-body-1">&ldquo;{search}&rdquo;</strong>.
                </span>
              ) : (
                <span className="text-body-2">Zero results for this date range.</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
